import React from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useController } from 'react-hook-form'

import invalid from '../../../assets/images/invalid.svg'
import { InputAutocomplete } from './InputAutocomplete'

/**
 * InputSelect component renders a select or autocomplete input field with optional error handling.
 * It supports dynamic options and conditional rendering based on the field configuration.
 *
 * @module form/InputSelect
 * @description InputSelect component renders a select or autocomplete input field with optional error handling.
 * It supports dynamic options and conditional rendering based on the field configuration.
 * @param {Object} field - Configuration for the select input field, including name, placeholder, and size.
 * @param {Object} errors - Validation errors object, typically provided by react-hook-form.
 * @param {Function} register - Function to register the input field, typically provided by react-hook-form.
 * @param {Object} control - Control object provided by react-hook-form for managing form state.
 * @param {Object} mainData - Main data object for populating select options.
 * @param {Object} defaultData - Default data object used to populate initial field values.
 * @param {Object} parentData - Parent data object used for cascading dropdowns or dependent fields.
 *
 * @example
 * const field = {
 *   name: 'flavor',
 *   placeholder: 'Select a flavor',
 *   size: 6,
 *   hidden: false,
 *   isAutocomplete: false,
 *   optionArrayDependency: [
 *     { id: 1, name: 'Chocolate' },
 *     { id: 2, name: 'Strawberry' },
 *   ],
 * };
 * const errors = {};
 * const register = () => {};
 * const control = {};
 * const mainData = {};
 * const defaultData = {};
 * const parentData = {};
 *
 * <InputSelect
 *   field={field}
 *   errors={errors}
 *   register={register}
 *   control={control}
 *   mainData={mainData}
 *   defaultData={defaultData}
 *   parentData={parentData}
 * />
 *
 * @returns {React.Element} The rendered InputSelect component.
 */
export const InputSelect = ({
  field,
  errors,
  register,
  control,
  mainData,
  defaultData,
  parentData,
}) => {
  const {
    field: { value: fieldValue, onChange: fieldOnChange, ...restField },
  } = useController({ name: field.name, control })

  const options = [
    { id: 1, value: 'chocolate', name: 'Chocolate' },
    { id: 2, value: 'strawberry', name: 'Strawberry' },
    { id: 3, value: 'vanilla', name: 'Vanilla' },
  ]

  try {
    if (field.isAutocomplete) {
      const searchOptions = []
      field.optionAPIDependency &&
        JSON.parse(sessionStorage.getItem('auth')).state.dependeciesFormAPIS[
          field.compOptionAPIDependency
        ]?.formData.map((option) =>
          searchOptions.push({
            value: option[field.compId],
            label: option[field.compName],
          }),
        )
      return (
        <div
          className={`trtui-form-group trtui-col-md-${field.size} trtui-has-validation`}
          style={{ display: field.hidden ? 'none' : 'block' }}
        >
          <label className="trtui-form-control-label">{field.placeholder}</label>
          <Select
            className="select-input"
            placeholder={field.placeholder}
            isClearable
            options={searchOptions}
            value={fieldValue ? searchOptions.find((x) => x.value === fieldValue) : fieldValue}
            onChange={(option) => fieldOnChange(option ? option.value : option)}
            {...restField}
          />
          {errors[field.name] && (
            <div className="trtui-invalid-feedback">{errors[field.name].message}</div>
          )}
        </div>
      )
    } else {
      return (
        <div
          className={`trtui-form-group trtui-col-md-${field.size} trtui-has-validation`}
          style={{ display: field.hidden ? 'none' : 'block' }}
        >
          <label className="trtui-form-control-label">{field.placeholder}</label>
          {field.isAutocomplete ? (
            <InputAutocomplete
              register={register}
              field={field}
              errors={errors}
              suggestions={options}
            />
          ) : (
            <select
              className={`trtui-form-control ${errors[field.name] && 'trtui-is-invalid'}`}
              style={{
                paddingRight: 'calc(1.5em + 1rem)',
                backgroundImage: errors[field.name] && `url("${invalid}")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right calc(0.375em + 0.25rem) center',
                backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
              }}
              {...register(field.name)}
              defaultValue={defaultData ? defaultData[field.name] : null}
            >
              <option value={''}>Select {field.placeholder}...</option>
              {field.optionArrayDependency &&
                field.compOptionArrayDependency.map((option, optionIndex) => (
                  <option
                    key={optionIndex}
                    value={option.id}
                    disabled={option.disabled ? true : false}
                  >
                    {option.name}
                  </option>
                ))}
              {field.valFromObjOfParentData &&
                parentData[field.obj[0]] &&
                parentData[field.obj[0]].map((option, optionIndex) => (
                  <option key={optionIndex} value={option[field.compId]}>
                    {Array.isArray(field.compName)
                      ? option[field.compName[0]][field.compName[1]]
                      : option[field.obj[1]]}
                  </option>
                ))}
              {field.valFromObj &&
                mainData[field.obj[0]] &&
                mainData[field.obj[0]].map((option, optionIndex) => (
                  <option key={optionIndex} value={option[field.compId]}>
                    {option[field.obj[1]]}
                  </option>
                ))}
              {field.optionAPIDependencyChild &&
                JSON.parse(sessionStorage.getItem('auth')).state.dependeciesFormAPIS[
                  field.compOptionAPIDependencyChild
                ]?.formData.map((option, optionIndex) => (
                  <option key={optionIndex} value={option[field.childData][field.compId]}>
                    {option[field.childData][field.compName]}
                  </option>
                ))}
              {field.optionAPIDependency &&
                JSON.parse(sessionStorage.getItem('auth')).state.dependeciesFormAPIS[
                  field.compOptionAPIDependency
                ]?.formData.map((option, optionIndex) => (
                  <option key={optionIndex} value={option[field.compId]}>
                    {option[field.compName]}
                  </option>
                ))}
              {field.childObjectOfAPIDependency &&
                JSON.parse(sessionStorage.getItem('auth')).state.dependeciesFormAPIS[
                  field.compChildObjectOfAPIDependency[0]
                ][field.compChildObjectOfAPIDependency[1]].map((option, optionIndex) => (
                  <option key={optionIndex} value={option[field.compId]}>
                    {option[field.compName]}
                  </option>
                ))}
            </select>
          )}
          {errors[field.name] && (
            <div className="trtui-invalid-feedback">{errors[field.name].message}</div>
          )}
        </div>
      )
    }
  } catch (err) {
    return <div>Error{console.log(err)}</div>
  }
}

InputSelect.propTypes = {
  /**
   * Configuration object for the select input field.
   */
  field: PropTypes.object.isRequired,

  /**
   * Validation errors object, typically provided by react-hook-form.
   */
  errors: PropTypes.object.isRequired,

  /**
   * Function to register the input field, typically provided by react-hook-form.
   */
  register: PropTypes.func.isRequired,

  /**
   * Control object provided by react-hook-form for managing form state.
   */
  control: PropTypes.object.isRequired,

  /**
   * Main data object for populating select options.
   */
  mainData: PropTypes.object.isRequired,

  /**
   * Default data object used to populate initial field values.
   */
  defaultData: PropTypes.object.isRequired,

  /**
   * Parent data object used for cascading dropdowns or dependent fields.
   */
  parentData: PropTypes.object.isRequired,
}
